import { useApolloClient } from "@apollo/client"
import { IconButton } from "@chakra-ui/react"
import { flattenEdges } from "@pathwright/utils"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import { relativeTimeColorScheme } from "@pathwright/ux/src/utils/color"
import { useMemo } from "react"
import {
  PathDocument,
  PathItemFragment,
  PathItemFragmentDoc,
  usePathLazyQuery,
  usePathQuery,
  useUnarchivePathItemMutation
} from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { useSyncPlanContext } from "../sync/SyncPlanContext"
import { getPathQueryVariables } from "./utils"

function useUnarchivePathItem(item: PathItemFragment) {
  const client = useApolloClient()
  const cohortId = item.context?.cohort_id!
  const anyArchivedTimePathQueryVariables = getPathQueryVariables({
    cohortId,
    navigatorPathFilters: { archived_time: ["any"] }
  })
  const defaultPathQueryVariables = getPathQueryVariables({
    cohortId,
    navigatorPathFilters: { archived_time: [] }
  })

  // From cache-only, since the path navigator may or may not be including
  // archived items, depending on the filter state. Though, we could just
  // get that setting from context if we wanted.
  const pathQuery = usePathQuery({
    variables: anyArchivedTimePathQueryVariables,
    fetchPolicy: "cache-only"
  })

  // Get the items, so we can check if the (potential) parent is archived or not.
  const path = pathQuery.data?.path
  const items = useMemo(
    () => flattenEdges(path?.items) || [],
    [path?.items]
  ) as PathItemFragment[]

  // For refetching the base path after unarchiving an item.
  const [refetchNonArchivedPath] = usePathLazyQuery({
    variables: defaultPathQueryVariables,
    fetchPolicy: "network-only"
  })

  // For refetching the "full" path (archived + non-archived items) after unarchiving an item.
  const [refetchArchivedPath] = usePathLazyQuery({
    variables: anyArchivedTimePathQueryVariables,
    fetchPolicy: "network-only"
  })

  const { refetchSyncPlanQueries } = useSyncPlanContext()
  // Refetching both path queries (for archived and archived + non-archived items).
  // Consider consolidating the unarchive path item mutation with refetchPath into
  // one function or hook.
  async function onUnarchivePathItem() {
    await Promise.all([
      refetchNonArchivedPath(),
      refetchArchivedPath(),
      refetchSyncPlanQueries?.()
    ])
  }

  const [unarchivePathItem] = useUnarchivePathItemMutation({
    variables: {
      id: item.source_id,
      pathId: item.path_id
    },
    optimisticResponse: {
      unarchivePathItem: {
        ...item,
        archived_time: null
      }
    },
    onCompleted: onUnarchivePathItem
  })

  async function handleUnarchivePathItem() {
    // Get the previous data for rolling back if the mutation fails.
    const previousData = structuredClone(pathQuery.data)

    // NOTE: we could also get the item itself here rather than doing so in
    // the optimistic response.
    const childItems =
      pathQuery.data?.path?.items?.edges?.reduce(
        // Remove the item's children (if any).
        (acc, edge) => {
          if (edge?.node!.parent_source_id === item.source_id) {
            acc.push(edge.node)
          }
          return acc
        },
        [] as PathItemFragment[]
      ) || ([] as PathItemFragment[])

    // For each child item, we need to remove the arhived time in the cache.
    childItems.forEach((childItem) => {
      client.writeFragment({
        id: `PathItem:${childItem.id}`,
        fragmentName: "PathItem",
        fragment: PathItemFragmentDoc,
        data: {
          ...childItem,
          archived_time: null
        }
      })
    })

    await unarchivePathItem({
      // If the mutation fails, restore the previous cached path query data.
      onError() {
        client.writeQuery({
          query: PathDocument,
          variables: anyArchivedTimePathQueryVariables,
          data: previousData
        })
      }
    })
  }

  // Can always unarchive a non-parented item, but can only unarchive a parented item
  // if its parent is not archived. To unarchive a child item whose parent is archived,
  // the parent must be unarchived.
  const itemCanBeUnarchived =
    !!item.archived_time &&
    (!item.parent_source_id ||
      !items.some(
        (i) => i.source_id === item.parent_source_id && i.archived_time
      ))

  return { unarchivePathItem: handleUnarchivePathItem, itemCanBeUnarchived }
}

// Unarchive button for an archived item, if it can be unarchived.
// If it cannot be unarchived (because its parent is archived), show a non-interactive trash icon.
function _PathNavigatorUnarchiveItem({ item }: { item: PathItemFragment }) {
  const { unarchivePathItem, itemCanBeUnarchived } = useUnarchivePathItem(item)
  const { school } = usePathwrightContext()
  const archivedTime = new Intl.DateTimeFormat(school.ietf_language_tag!, {
    dateStyle: "full",
    timeStyle: "long"
  }).format(new Date(item.archived_time))
  const colorPalette = relativeTimeColorScheme(item.archived_time)

  return (
    <Tooltip
      title={`Archived on ${archivedTime}`}
      // Not appending to portaled parent in order to render above and outside the path navigator.
      // No longer suppoerted in Chakra UI v3. Docs say: Remove appendToParentPortal prop in favor of using the containerRef.
      // It seems we'd need to pass a containerRef prop to Tooltip for this to work.

      // portal={{ appendToParentPortal: false }}
      popover={{ positioning: { placement: "left" } }}
    >
      {itemCanBeUnarchived ? (
        <IconButton
          children={<Pathicon icon="trash-undo" />}
          variant="ghost"
          colorPalette={colorPalette}
          aria-label="Unarchive item"
          bg="none"
          _hover={{
            color: `${colorPalette}.400`
          }}
          _focus={{
            bg: "none"
          }}
          onClick={(e) => {
            // Prevent default navigation of path navigator item.
            e.preventDefault()
            e.stopPropagation()
            unarchivePathItem()
          }}
          minW={5}
          h={5}
          borderRadius="full"
        />
      ) : (
        <Pathicon icon="trash" color={`${colorPalette}.900`} minW={5} />
      )}
    </Tooltip>
  )
}

// Container for avoiding useUnarchivePathItem hook when item is not archived.
export function PathNavigatorUnarchiveItem({
  item
}: {
  item: PathItemFragment
}) {
  return item.archived_time ? <_PathNavigatorUnarchiveItem item={item} /> : null
}
